<template>
    <vx-expandable-card toggleable id="to" :active="props.active">
        <template v-slot:header>
            <div class="flex items-center pb-1 lg:pb-0">
                <div
                    class="inline-block pr-1.5 mr-2 border-y-0 border-l-0 border-r border-solid sm:pr-3 sm:mr-4"
                    :class="{
                        'border-r-transparent': !editable,
                        'border-r-slate-400': editable,
                    }"
                >
                    To
                </div>
                <div v-if="editable" data-test="summary" class="flex items-center gap-1.5">
                    <span class="text-xs font-semibold text-slate-700 leading-snug sm:text-base">
                        {{ audienceSummaryText }}
                    </span>
                </div>
            </div>
        </template>
        <template v-if="editable && !props.success" v-slot:headerRight>
            <vx-button @click.stop="emit('update:active', true)" color="muted" size="md">
                <span class="inline-block">Edit</span>
                <font-awesome-icon
                    widthAuto
                    class="text-base hidden! sm:inline-block!"
                    :icon="faPencil"
                ></font-awesome-icon>
            </vx-button>
        </template>
        <template v-slot:content>
            <vx-tabs v-if="onCorporate()" v-model="currentTab" size="lg">
                <vx-tab value="contacts">Number List</vx-tab>
                <vx-tab value="segments">Segments</vx-tab>
                <vx-tab value="external-contact-lists" v-if="FEATURES.external_contact_lists">External List</vx-tab>
                <vx-tab value="everyone">Everyone</vx-tab>
            </vx-tabs>

            <div v-else>
                <div class="flex flex-col gap-3 border bg-slate-100 border-white border-solid shadow rounded-lg p-4">
                    <div
                        v-if="audienceSelectionCount > 0"
                        class="flex items-center gap-1.5"
                        data-test="selection-summary"
                    >
                        <font-awesome-icon
                            v-if="props.modelValue.type === 'segments'"
                            :icon="faAddressBook"
                            class="text-xs text-slate-800"
                        />
                        <font-awesome-icon
                            v-if="props.modelValue.type === 'external-contact-lists'"
                            :icon="faBookUser"
                            class="text-xs text-slate-800"
                        />
                        <span class="text-xs font-bold text-slate-800 leading-snug">
                            {{ audienceSummaryText }}
                        </span>
                    </div>

                    <vx-button @click="showAudiencesModal = true" data-test="select-audiences-button" color="muted">
                        Choose Audience
                        <font-awesome-icon widthAuto :icon="faMagnifyingGlass" />
                    </vx-button>

                    <vx-alert v-if="error" class="my-3 self-stretch" :closeable="false" color="danger">{{
                        error
                    }}</vx-alert>
                </div>

                <vx-button
                    @click="confirm()"
                    color="primary"
                    size="lg"
                    block
                    class="mt-4 lg:mt-6"
                    data-test="to-confirm"
                >
                    Continue
                    <font-awesome-icon :icon="faArrowTurnDown" class="text-base" />
                </vx-button>
            </div>

            <vx-modal
                v-if="!onCorporate()"
                v-model:visible="showAudiencesModal"
                size="lg"
                :padding="false"
                data-test="audiences-modal"
            >
                <page-select-audiences
                    ref="audiencesSelector"
                    v-model="selectedAudiences"
                    :audienceType="props.campaignType"
                    :groupIds="props.groupIds"
                />

                <template v-slot:footer>
                    <div class="flex justify-end gap-4">
                        <vx-button @click="showAudiencesModal = false" color="muted" size="lg" class="px-5">
                            Cancel
                        </vx-button>
                        <vx-button
                            @click="handleAudiencesConfirm"
                            color="primary"
                            size="lg"
                            class="flex-grow"
                            :disabled="selectedAudiences.length === 0 || audiencesSelector?.hasMixedTypes"
                            data-test="confirm-audiences"
                        >
                            Confirm
                            <font-awesome-icon widthAuto :icon="faCircleCheck" />
                        </vx-button>
                    </div>
                </template>
            </vx-modal>

            <quick-blast-to-manual
                v-if="onCorporate() && currentTab === 'contacts'"
                :modelValue="props.modelValue.type === 'contacts' ? props.modelValue.data : []"
                :campaignType="props.campaignType"
                @update:modelValue="
                    emit('update:modelValue', {
                        type: 'contacts',
                        data: $event,
                    })
                "
                @continue="confirm()"
            ></quick-blast-to-manual>

            <template v-if="onCorporate() && currentTab === 'segments'">
                <div
                    class="max-w-full"
                    :class="{
                        'mt-4 lg:mt-6': onCorporate(),
                    }"
                >
                    <div class="mb-4">
                        <span class="text-slate-900 font-semibold text-sm block sm:text-base">
                            Segments to receive this message
                        </span>
                        <span class="text-slate-500 text-xs">
                            Segments filtered to match the audience type <strong>{{ props.campaignType }}</strong> that
                            you chose on the previous step.
                        </span>
                    </div>

                    <segments-selectable
                        size="lg"
                        :groupIds="props.groupIds"
                        :audienceType="props.campaignType"
                        :modelValue="props.modelValue.type === 'segments' ? props.modelValue.data : []"
                        @update:modelValue="
                            emit('update:modelValue', {
                                type: 'segments',
                                data: $event,
                            })
                        "
                    >
                    </segments-selectable>
                    <div class="text-xs text-slate-500 mt-4 flex items-center">
                        <font-awesome-icon
                            widthAuto
                            :icon="faCircleInfo"
                            class="text-gray text-sm mr-1"
                        ></font-awesome-icon>
                        <span class="flex flex-col items-start sm:block">
                            To edit or create segments, visit the&nbsp;
                            <span>
                                <a href="/contacts/segments" target="_blank" class="text-inherit underline"
                                    >segments page</a
                                >.
                            </span>
                        </span>
                    </div>

                    <vx-alert v-if="error" class="my-3" :closeable="false" color="danger">{{ error }}</vx-alert>
                    <confirm-button></confirm-button>
                </div>
            </template>

            <template v-if="onCorporate() && currentTab === 'external-contact-lists'">
                <div
                    class="max-w-full"
                    :class="{
                        'mt-4 lg:mt-6': onCorporate(),
                    }"
                >
                    <div class="mb-4">
                        <span class="text-slate-900 font-semibold text-sm block sm:text-base">
                            External Lists to receive this message
                        </span>
                    </div>

                    <external-contact-lists-selectable
                        size="lg"
                        :audienceType="props.campaignType"
                        :modelValue="props.modelValue.type === 'external-contact-lists' ? props.modelValue.data : []"
                        @update:modelValue="
                            emit('update:modelValue', {
                                type: 'external-contact-lists',
                                data: $event,
                            })
                        "
                    >
                    </external-contact-lists-selectable>

                    <vx-alert v-if="error" class="my-3" :closeable="false" color="danger">{{ error }}</vx-alert>
                    <confirm-button></confirm-button>
                </div>
            </template>

            <div v-if="onCorporate() && currentTab === 'everyone'" class="my-4 max-w-full lg:mt-6">
                <div class="text-sm p-4 lg:px-36 lg:py-10 lg:text-base" data-test="to-everyone-message">
                    This message will go to all contacts eligible to receive
                    <strong>{{ props.campaignType }}</strong> messages.
                </div>
                <vx-alert v-if="error" class="my-3" :closeable="false" color="danger">{{ error }}</vx-alert>
                <confirm-button></confirm-button>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import { VxAlert, VxExpandableCard, VxTab, VxTabs, VxButton, VxModal } from '@voxie/frontend-components';
import { ref, computed, h, inject, watch } from 'vue';
import QuickBlastToManual from './QuickBlastToManual.vue';
import SegmentsSelectable from '../general/segments/SegmentsSelectable.vue';
import PageSelectAudiences from './PageSelectAudiences.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    faCircleInfo,
    faArrowTurnDown,
    faCircleCheck,
    faAddressBook,
    faBookUser,
    faMagnifyingGlass,
    faPencil,
} from '@fortawesome/pro-solid-svg-icons';
import {
    AUDIENCE_TYPE_MARKETING,
    AUDIENCE_TYPE_TRANSACTIONAL,
    MAX_EXTERNAL_CONTACT_LISTS_PER_CAMPAIGN,
    MAX_SEGMENTS_PER_CAMPAIGN,
} from '~/constants/campaign';
import { useToasted } from '~/composables/useToasted';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import ExternalContactListsSelectable from '~/components/general/external-contact-lists/ExternalContactListsSelectable.vue';
import { pluralized } from '../filters';

const FEATURES = inject('FEATURES');

const toasted = useToasted();
const { onCorporate } = useCurrentGroup();

const confirmButton = () => {
    return h(
        VxButton,
        {
            onClick: () => confirm(),
            class: 'mt-4 lg:mt-6',
            size: 'lg',
            block: true,
            'data-test': 'to-confirm',
        },
        () => ['Continue', h(FontAwesomeIcon, { icon: faArrowTurnDown, class: 'text-base' })]
    );
};

const props = defineProps({
    modelValue: {
        type: Object,
        default: () => ({}),
    },
    groupIds: { type: Array, required: false },
    active: {
        type: Boolean,
        default: false,
    },
    success: {
        type: Boolean,
        default: true,
    },
    estimation: {
        required: false,
    },
    estimationLoading: {
        type: Boolean,
        default: false,
    },
    campaignType: {
        type: String,
        default: undefined,
        validator: (campaignType) =>
            [AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL, undefined].includes(campaignType),
    },
});

const currentTab = ref(props.modelValue.type);

const editable = computed(() => !props.active && (hasConfirmed.value || props.modelValue.data?.length));
const hasConfirmed = ref(false);

const error = ref('');

const showAudiencesModal = ref(false);
const selectedAudiences = ref([]);
const audiencesSelector = ref(null);

watch(
    () => props.modelValue,
    (newVal) => {
        if (['segments', 'external-contact-lists'].includes(newVal.type) && newVal.data) {
            selectedAudiences.value = newVal.data.map((item) => ({
                ...item,
                sourceType: newVal.type === 'segments' ? 'segment' : 'external-list',
                label: item.label || `${item.id} - ${item.name}`,
            }));
        }
    },
    { immediate: true, deep: true }
);

const audienceSelectionCount = computed(() => {
    return ['segments', 'external-contact-lists'].includes(currentTab.value) ? props.modelValue.data?.length || 0 : 0;
});

const validateAudienceSelection = (type, data) => {
    if (!data?.length) {
        return { valid: false, error: `You haven't added any ${type}` };
    }

    if (type === 'segments' && data.length > MAX_SEGMENTS_PER_CAMPAIGN) {
        return {
            valid: false,
            error: `A campaign can only contain up to ${MAX_SEGMENTS_PER_CAMPAIGN} segments.`,
        };
    }

    if (type === 'external-contact-lists' && data.length > MAX_EXTERNAL_CONTACT_LISTS_PER_CAMPAIGN) {
        return {
            valid: false,
            error: `A campaign can only contain up to ${MAX_EXTERNAL_CONTACT_LISTS_PER_CAMPAIGN} external contact lists.`,
        };
    }

    return { valid: true, error: '' };
};

const handleAudiencesConfirm = () => {
    if (!selectedAudiences.value.length) {
        error.value = "You haven't selected any audiences";
        toasted.error(error.value);
        return;
    }

    const type = selectedAudiences.value[0].sourceType === 'segment' ? 'segments' : 'external-contact-lists';
    const data = selectedAudiences.value.map(({ sourceType: _sourceType, ...item }) => item);

    const validation = validateAudienceSelection(type, data);
    if (!validation.valid) {
        error.value = validation.error;
        toasted.error(validation.error);
        return;
    }

    error.value = '';
    emit('update:modelValue', { type, data });
    currentTab.value = type;
    showAudiencesModal.value = false;
    hasConfirmed.value = true;
};

const audienceSummaryText = computed(() => {
    const labels = {
        segments: 'Segment',
        contacts: 'Number',
        'external-contact-lists': 'External List',
    };

    if (props.modelValue.type === 'everyone') {
        return 'Everyone';
    }

    const label = labels[props.modelValue.type];
    const count = props.modelValue.data?.length || 0;

    return pluralized(label, count, true);
});

const confirm = () => {
    if (currentTab.value === 'everyone') {
        emit('update:modelValue', { type: 'everyone', data: [] });
        emit('continue');
        hasConfirmed.value = true;
        return;
    }

    if (props.modelValue.type !== currentTab.value) {
        error.value = "You haven't added any " + currentTab.value;
        toasted.error("You haven't added any " + currentTab.value);
        return;
    }

    const validation = validateAudienceSelection(props.modelValue.type, props.modelValue.data);
    if (!validation.valid) {
        error.value = validation.error;
        toasted.error(validation.error);
        return;
    }

    hasConfirmed.value = true;
    emit('continue');
};

const emit = defineEmits(['update:active', 'update:modelValue', 'continue']);
</script>
